import React, { FC, ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import { formatNumber } from 'humanize-plus';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';

import styles from './pricingDetailsStyles.scss';

import Direction from 'buyside/components/presentation/Direction/direction';
import HoverOverTooltip from 'buyside/components/presentation/Tooltips/HoverOverTooltip/hoverOverTooltip';
import InfoTable from 'buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import PricingData from './pricingData';
import PriceTargetOverlay, { TargetPrice } from './priceTargetOverlay';
import PricingDetailsLoading from 'buyside/components/presentation/IdeaShowPage/RightSidebar/PricingDetails/pricingDetailsLoading';

import {
  IdeaCoreDocument,
  IdeaCoreQuery,
  IdeaPositionsDocument,
  IdeaPositionsPricingDocument,
  IdeaPositionsPricingQuery,
  IdeaPositionsQuery,
  IdeaUpdatesQuery,
  Maybe,
  useIdeaUpdatesQuery
} from 'interfaces/graphql-typings';
enum PriceDetailLabels {
  Direction = 'DIRECTION',
  RecentPrice = 'RECENT PRICE',
  TargetPrice = 'TARGET PRICE',
  InitialPrice = 'INITIAL PRICE',
  ClosingPrice = 'CLOSING PRICE',
  Weighting = 'WEIGHTING',
  Symbol = 'SYMBOL'
}

const createPriceDetailItems = (
  closedAt: Maybe<string>,
  ideaUpdates: IdeaUpdatesQuery['idea']['ideaUpdates'],
  initialPriceTarget: Maybe<number>,
  isPairTrade: boolean,
  openedAt: Maybe<string>,
  pairRatio: Maybe<Maybe<number>[]>,
  positions: IdeaPositionsQuery['idea']['positions'],
  positionsPricing: Record<string, IdeaPositionsPricingQuery['idea']['positions'][number]>,
  canViewIdeaFilterDetails: boolean
): { label: PriceDetailLabels; data: string | JSX.Element | JSX.Element[] }[] => {
  const [primaryPosition] = positions;
  const primaryCurrencyCode = primaryPosition.asset?.currencyCode || '';
  const unadjustedPriceTarget = primaryPosition.priceTarget;

  const isCurrencySubunit = primaryPosition.isCurrencySubunit;
  const priceTarget =
    isCurrencySubunit && unadjustedPriceTarget
      ? unadjustedPriceTarget * 100
      : unadjustedPriceTarget;

  let targetPriceHistory: TargetPrice[] = [];
  if (canViewIdeaFilterDetails && !isEmpty(ideaUpdates)) {
    targetPriceHistory =
      ideaUpdates?.reduce((targetPriceHistory, ideaUpdate): TargetPrice[] => {
        if (!ideaUpdate) return targetPriceHistory;
        const { removedPriceTarget, priceTarget, createdAt } = ideaUpdate;
        const updatePriceTarget = priceTarget || 0;
        return removedPriceTarget
          ? [
              ...targetPriceHistory,
              {
                priceTarget: isCurrencySubunit ? updatePriceTarget * 100 : updatePriceTarget,
                createdAt
              }
            ]
          : targetPriceHistory;
      }, targetPriceHistory) || [];

    if (!isEmpty(targetPriceHistory) && initialPriceTarget) {
      targetPriceHistory = [
        {
          priceTarget: isCurrencySubunit ? initialPriceTarget * 100 : initialPriceTarget,
          createdAt: openedAt
        },
        ...targetPriceHistory
      ];
    }
  }

  return [
    isPairTrade
      ? {
          label: PriceDetailLabels.Symbol,
          data: positions.map(
            ({ id, asset }): ReactElement => {
              const symbol = asset?.symbol;

              return (
                <div key={id} className={styles.priceData}>
                  {symbol}
                </div>
              );
            }
          )
        }
      : null,
    {
      label: PriceDetailLabels.Direction,
      data: positions.map(
        ({ id, direction }): ReactElement => (
          <div key={id} className={styles.priceData}>
            <Direction direction={direction} redacted={!canViewIdeaFilterDetails} />
          </div>
        )
      )
    },
    isPairTrade
      ? {
          label: PriceDetailLabels.Weighting,
          data: pairRatio?.map(
            (ratio, idx): ReactElement => (
              <div key={idx} className={styles.priceData}>
                {ratio}
              </div>
            )
          )
        }
      : null,
    closedAt
      ? null
      : {
          label: PriceDetailLabels.RecentPrice,
          data: positions.map(
            ({ id, asset, isCurrencySubunit }): ReactElement => {
              const { latestPrice, latestPriceDate } = positionsPricing[id];
              const currencyCode = asset?.currencyCode;
              const price = isCurrencySubunit && latestPrice ? latestPrice * 100 : latestPrice;

              return (
                <div key={id} className={styles.priceData}>
                  <PricingData
                    price={
                      price && !closedAt ? (
                        `${formatNumber(price, 2)} ${currencyCode}`
                      ) : (
                        <div className={styles.pending}>Pending...</div>
                      )
                    }
                    time={
                      latestPriceDate && !closedAt
                        ? moment(latestPriceDate).format('M/D/YY [at] [11:59PM]')
                        : null
                    }
                  />
                </div>
              );
            }
          )
        },
    isPairTrade
      ? null
      : {
          label: PriceDetailLabels.TargetPrice,
          data: isEmpty(targetPriceHistory) ? (
            priceTarget || priceTarget === 0.0 ? (
              `${formatNumber(priceTarget, 2)} ${primaryCurrencyCode}`
            ) : (
              'n/a'
            )
          ) : (
            <HoverOverTooltip
              overlay={
                <PriceTargetOverlay
                  targetPriceList={targetPriceHistory}
                  currencyCode={primaryCurrencyCode}
                />
              }
            >
              {formatNumber(priceTarget || 0, 2)} {primaryCurrencyCode}
            </HoverOverTooltip>
          )
        },
    {
      label: PriceDetailLabels.InitialPrice,
      data: positions.map(
        ({ id, asset, isCurrencySubunit }): ReactElement => {
          const openingPrice = positionsPricing[id].openingPrice;
          const currencyCode = asset?.currencyCode;
          const price = isCurrencySubunit && openingPrice ? openingPrice * 100 : openingPrice;

          return (
            <div key={id} className={styles.priceData}>
              <PricingData
                price={
                  price ? (
                    `${formatNumber(price, 2)} ${currencyCode}`
                  ) : (
                    <div className={styles.pending}>Pending...</div>
                  )
                }
                time={openedAt ? moment(openedAt).format('M/D/YY [at] [12:00AM]') : null}
              />
            </div>
          );
        }
      )
    },
    closedAt
      ? {
          label: PriceDetailLabels.ClosingPrice,
          data: positions.map(
            ({ id, asset, closingPrice, isCurrencySubunit }): ReactElement => {
              const currencyCode = asset?.currencyCode;
              const price = isCurrencySubunit && closingPrice ? closingPrice * 100 : closingPrice;
              return (
                <div key={id} className={styles.priceData}>
                  <PricingData
                    price={price ? `${formatNumber(price, 2)} ${currencyCode}` : 'N/A'}
                    time={
                      closedAt ? moment(closedAt, 'M/D/YYYY').format('M/D/YY [at] [12:00AM]') : null
                    }
                  />
                </div>
              );
            }
          )
        }
      : null
  ].filter((e): Boolean => Boolean(e)) as {
    label: PriceDetailLabels;
    data: string | JSX.Element | JSX.Element[];
  }[];
};

const PricingDetails: FC<{
  ideaId: string;
  loading: boolean;
}> = ({ ideaId, loading }) => {
  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCoreDocument,
    variables: { ideaId }
  })?.idea;

  const cachedPosIdea = client.readQuery<IdeaPositionsQuery>({
    query: IdeaPositionsDocument,
    variables: { ideaId }
  })?.idea;

  const cachedPosPricingIdea = client.readQuery<IdeaPositionsPricingQuery>({
    query: IdeaPositionsPricingDocument,
    variables: { ideaId }
  })?.idea;

  const { data: updatesData, loading: updatesLoading, error: updatesError } = useIdeaUpdatesQuery({
    variables: { id: ideaId }
  });

  const { positions } = cachedPosIdea || {};

  const positionsPricing = useMemo(() => {
    if (!cachedPosPricingIdea?.positions) return null;

    return cachedPosPricingIdea?.positions.reduce(
      (acc: Record<string, IdeaPositionsPricingQuery['idea']['positions'][number]>, position) => {
        acc[position.id] = position;
        return acc;
      },
      {}
    );
  }, [cachedPosPricingIdea]);

  if (
    loading ||
    updatesLoading ||
    !cachedIdea ||
    !positions ||
    !positionsPricing ||
    !updatesData?.idea
  )
    return <PricingDetailsLoading />;

  const { ideaUpdates, benchmarkUpdate } = updatesData.idea;
  const {
    canCurrentUserViewIdeaFilterDetails: canViewIdeaFilterDetails,
    closedAt,
    openedAt,
    pairRatio
  } = cachedIdea;

  if (updatesError) throw updatesError;

  const initialPriceTarget = benchmarkUpdate?.priceTarget || null;
  const isPairTrade = positions.length > 1;

  return (
    <InfoTable title="Pricing Details">
      {({ labelStyle, dataStyle, blur }): ReactElement[] => {
        const ideaFilterDetails = [PriceDetailLabels.TargetPrice, PriceDetailLabels.Direction];

        return createPriceDetailItems(
          closedAt,
          ideaUpdates,
          initialPriceTarget,
          isPairTrade,
          openedAt,
          pairRatio,
          positions,
          positionsPricing,
          canViewIdeaFilterDetails
        ).map(
          ({ label, data }, idx): ReactElement => (
            <div key={idx} className={styles.tableRow}>
              <div className={labelStyle}>{label}</div>
              <div
                data-testid={label
                  .toLowerCase()
                  .split(' ')
                  .join('-')}
                className={classNames(dataStyle, {
                  [blur]: ideaFilterDetails.includes(label) && !canViewIdeaFilterDetails
                })}
              >
                <div
                  className={classNames({
                    [styles.twoCol]: isPairTrade,
                    [styles.oneCol]: !isPairTrade
                  })}
                >
                  {data}
                </div>
              </div>
            </div>
          )
        );
      }}
    </InfoTable>
  );
};

export default PricingDetails;
